import React, {useCallback, useMemo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {UAProfileKeysInfo} from '../../store/types/Devices';
import {Colors} from '../../styles/Colors';
import {useTranslation} from 'react-i18next';
import {ChevronLeft, ChevronRight} from '@material-ui/icons';
import CustomizedIconButton from '../../components/IconButton/IconButton';

export type DeviceProfileKeysPagerProps = {
    keys: UAProfileKeysInfo;
    position?: 'top' | 'bottom';
    currentPage: number
    setCurrentPage: (value: number) => void
};

export const useStyles = makeStyles(() => ({
    root: {
        width: 212,
        height: 24,
        borderRadius: 42,
        borderWidth: 1,
        paddingTop: 10,
        paddingRight: 12,
        paddingBottom: 10,
        paddingLeft: 24,
        gap: '16px',
        background: 'white',
        boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
        border: '1px solid rgba(221, 225, 231, 0.5)',
        position: 'absolute',
        opacity: 1,
    },

    hidden: {
        display: 'none'
    },

    pagesContainer: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center'
    },

    keysPageText: {
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '24px',
        verticalAlign: 'middle',
        color: Colors.Gray9,
        userSelect: 'none',
        marginRight: 8,
        marginTop: 3,
    },

    pageCounter: {
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '24px',
        verticalAlign: 'middle',
        color: Colors.Text,
        userSelect: 'none',
        marginTop: 3,
    },

    navigation: {
        width: 64,
        height: 24,
        paddingLeft: 8,
        paddingRight: 8,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        verticalAlign: 'middle',
        alignItems: 'center',
    },

    navigationItem: {
        cursor: 'pointer',
        padding: 0,
    }
}));

const DeviceProfileKeysPager: React.FC<DeviceProfileKeysPagerProps> = ({
    keys,
    position,
    currentPage = 0,
    setCurrentPage
}) => {
    const classes = useStyles();
    const {t} = useTranslation();

    const isNoKeys = useMemo(() => {
        return !keys || !keys.total_keys || !keys.pager;
    }, [keys]);

    const maxPages = useMemo(() => {
        if(isNoKeys) return 0;
        let pages = Math.trunc(keys.total_keys / keys.pager);
        if ((keys.total_keys % keys.pager) !== 0) {
            pages = pages + 1;
        }
        return pages;
    }, [isNoKeys, keys]);

    const onChevronClick = useCallback((direction: 'left' | 'right') => {
        if(direction === 'left' && currentPage > 0) {
            setCurrentPage(currentPage - 1);
        }
        else if(direction === 'right' && currentPage < (maxPages - 1)) {
            setCurrentPage(currentPage + 1);
        }
    }, [currentPage, setCurrentPage]);

    if(isNoKeys) {
        return (<div className={classes.hidden}></div>);
    }

    const topOfButtons = useMemo(() => {
        const ordered = [...keys.keys].sort((a, b) => a.position.y - b.position.y);
        return ordered.length
            ? ordered[0].position.y
            : 0;
    }, [keys]);
    
    const bottomOfButtons = useMemo(() => {
        const ordered = [...keys.keys].sort((a, b) => b.position.y - a.position.y);
        return ordered.length
            ? ordered[0].position.y
            : 0;
    }, [keys]);
    
    const leftOfButtons = useMemo(() => {
        const ordered = [...keys.keys].sort((a, b) => a.position.x - b.position.x);
        return ordered.length
            ? ordered[0].position.x
            : 0;
    }, [keys]);
    
    const rigthOfButtons = useMemo(() => {
        const ordered = [...keys.keys].sort((a, b) => b.position.x - a.position.x);
        return ordered.length
            ? ordered[0].position.x
            : 0;
    }, [keys]);

    const rootWidth = 212 + 12 + 24 + 2;
    const rootHeight = 24 + 10 + 10 + 2;
    const scale = 0.5;

    const leftPositionOfRoot = useMemo(() => {
        const middleX = leftOfButtons + ((rigthOfButtons - leftOfButtons) / 2);
        return (middleX * scale) - (rootWidth / 2) + 10;
    }, [bottomOfButtons, leftOfButtons, topOfButtons, rigthOfButtons, position]);

    const topPositionOfRoot = useMemo(() => {
        return position === 'top'
            ? ((topOfButtons * scale) - rootHeight)
            : ((bottomOfButtons * scale) + 30);

    }, [bottomOfButtons, leftOfButtons, topOfButtons, rigthOfButtons, position]);

    return (<div className={classes.root} style={{
        left: leftPositionOfRoot + "px",
        top: topPositionOfRoot + "px",
    }}>
        <div className={classes.pagesContainer}>
            <div className={classes.keysPageText}>
                {t('screens:deviceProfiles.keysPage')}
            </div>
            <div className={classes.pageCounter}>
                {t('screens:deviceProfiles.pageCounterFormat', {
                    currentPage: currentPage + 1,
                    maxPages: maxPages
                })}
            </div>
            <div className={classes.navigation}>
                <CustomizedIconButton
                    className={classes.navigationItem}
                    onClick={() => onChevronClick('left')}
                    skipPermission
                    disabled={currentPage === 0}
                >
                    <ChevronLeft htmlColor={Colors.Text3} opacity={currentPage === 0 ? 0.4 : 1} />
                </CustomizedIconButton>
                <CustomizedIconButton
                    className={classes.navigationItem}
                    onClick={() => onChevronClick('right')}
                    skipPermission
                    disabled={currentPage === (maxPages - 1)}
                >
                    <ChevronRight htmlColor={Colors.Text3} opacity={(currentPage === (maxPages - 1)) ? 0.4 : 1} />
                </CustomizedIconButton>
            </div>
        </div>
    </div>);
};
    
export default DeviceProfileKeysPager;